import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { FileText, Snowflake, Thermometer, AlertTriangle, ShieldCheck, Clock, XCircle, HelpCircle } from "lucide-react";

const CONSERV = { frigo: "Frigorifero (0–4°C)", ambiente: "Temperatura ambiente", congelatore: "Congelatore (−18°C)", abbattitore: "Abbattuto e conservato" };

const COL = { bg: "#F5F3EE", ink: "#1B1B1B", sub: "#4A4A4A", gold: "#B8902E", header: "#1C1C1E" };

function expiryStatus(dateStr) {
  if (!dateStr) return { key: "na", label: "SCADENZA DA DEFINIRE", Icon: HelpCircle, fg: "#4A4A4A", bg: "#E7E4DC", bd: "#C9C4B6" };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { key: "na", label: "SCADENZA DA DEFINIRE", Icon: HelpCircle, fg: "#4A4A4A", bg: "#E7E4DC", bd: "#C9C4B6" };
  const today = new Date(); today.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return { key: "exp", label: "SCADUTO", Icon: XCircle, fg: "#7F1D1D", bg: "#FADCDC", bd: "#E5A3A3" };
  if (diff <= 3) return { key: "soon", label: "IN SCADENZA", Icon: Clock, fg: "#7A4A00", bg: "#FBEBCB", bd: "#E6C784" };
  return { key: "ok", label: "VALIDO", Icon: ShieldCheck, fg: "#14532D", bg: "#D6EBD9", bd: "#9FCBA6" };
}

const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); };

function StatusBadge({ s }) {
  const { Icon } = s;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border" style={{ color: s.fg, backgroundColor: s.bg, borderColor: s.bd }} data-testid={`expiry-${s.key}`}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />{s.label}
    </span>
  );
}

export default function PublicSheet() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/public/product-sheet/${id}`).then(({ data }) => setS(data)).catch(() => setErr(true)); }, [id]);

  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ backgroundColor: COL.bg, color: COL.sub }}>Scheda non trovata o non più disponibile.</div>;
  if (!s) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COL.bg, color: COL.sub }}>Caricamento…</div>;

  const sheetStatus = expiryStatus(s.expiry);
  const ings = s.ingredients || [];

  const Field = ({ label, value, icon }) => (
    <div className="flex items-baseline justify-between gap-3 py-2.5 border-b" style={{ borderColor: "#E2DFD6" }}>
      <span className="text-sm inline-flex items-center gap-1.5" style={{ color: COL.sub }}>{icon}{label}</span>
      <span className="text-sm font-semibold text-right" style={{ color: COL.ink }}>{value || "—"}</span>
    </div>
  );

  return (
    <div className="min-h-screen py-5 px-3 sm:px-4" style={{ backgroundColor: COL.bg }} data-testid="public-sheet">
      <div className="max-w-xl mx-auto rounded-2xl overflow-hidden shadow-md" style={{ backgroundColor: "#FFFFFF", border: `1px solid #E2DFD6` }}>
        {/* HEADER */}
        <div className="px-5 py-5" style={{ backgroundColor: COL.header }}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em]" style={{ color: COL.gold }}>{s.business?.name || "Dogana 2.0"}</p>
          <h1 className="text-white text-xl font-bold flex items-center gap-2 mt-1.5">
            <FileText className="w-5 h-5" style={{ color: COL.gold }} /> Scheda di rintracciabilità
          </h1>
        </div>

        <div className="p-5">
          <h2 className="text-2xl font-extrabold leading-tight" style={{ color: COL.ink }} data-testid="public-sheet-name">{s.name}</h2>
          <p className="text-xs mt-1 mb-4" style={{ color: COL.sub }}>Lotto interno: <b style={{ color: COL.ink }}>{s.lot_number}</b></p>

          <Field label="Prodotto il" value={`${fmtDate(s.prep_date)}${s.prep_time ? " · " + s.prep_time : ""}`} />
          <div className="flex items-center justify-between gap-3 py-2.5 border-b" style={{ borderColor: "#E2DFD6" }}>
            <span className="text-sm" style={{ color: COL.sub }}>Consumare entro</span>
            <span className="flex items-center gap-2 text-right">
              <b className="text-sm" style={{ color: COL.ink }}>{fmtDate(s.expiry)}</b>
              <StatusBadge s={sheetStatus} />
            </span>
          </div>
          <Field label="Conservazione" icon={<Snowflake className="w-3.5 h-3.5" />} value={`${CONSERV[s.conservation] || s.conservation || "—"}${s.conservation_temp ? " · " + s.conservation_temp : ""}`} />
          {(s.blast_start || s.blast_end) && <Field label="Abbattimento" icon={<Thermometer className="w-3.5 h-3.5" />} value={`${s.blast_start || "—"} – ${s.blast_end || "—"}`} />}
          <Field label="Responsabile" value={s.operator} />

          {/* INGREDIENTI */}
          <h3 className="font-bold mt-6 mb-3 text-base" style={{ color: COL.ink }}>Ingredienti / lotti utilizzati</h3>

          {/* MOBILE: card verticali */}
          <div className="space-y-3 sm:hidden">
            {ings.length === 0 && <p className="text-sm text-center py-4" style={{ color: COL.sub }}>Nessun ingrediente</p>}
            {ings.map((it, i) => {
              const st = expiryStatus(it.expiry);
              return (
                <div key={i} className="rounded-xl p-3.5" style={{ backgroundColor: "#FBFAF7", border: `1px solid #E2DFD6`, borderLeft: `4px solid ${COL.gold}` }} data-testid={`ing-card-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-bold text-[15px] leading-snug" style={{ color: COL.ink }}>{it.name}</p>
                    <StatusBadge s={st} />
                  </div>
                  <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                    {it.supplier && (<><dt style={{ color: COL.sub }}>Fornitore</dt><dd className="font-medium text-right" style={{ color: COL.ink }}>{it.supplier}</dd></>)}
                    <dt style={{ color: COL.sub }}>Lotto</dt><dd className="font-medium text-right" style={{ color: COL.ink }}>{it.lotto || "—"}</dd>
                    {(it.entry_date || it.ingresso) && (<><dt style={{ color: COL.sub }}>Ingresso</dt><dd className="font-medium text-right" style={{ color: COL.ink }}>{fmtDate(it.entry_date || it.ingresso)}</dd></>)}
                    <dt style={{ color: COL.sub }}>Scadenza</dt><dd className="font-medium text-right" style={{ color: COL.ink }}>{fmtDate(it.expiry)}</dd>
                    {(it.quantity ? true : false) && (<><dt style={{ color: COL.sub }}>Q.tà usata</dt><dd className="font-medium text-right" style={{ color: COL.ink }}>{it.quantity} {it.unit || ""}</dd></>)}
                  </dl>
                </div>
              );
            })}
          </div>

          {/* DESKTOP: tabella */}
          <div className="hidden sm:block rounded-xl overflow-hidden" style={{ border: `1px solid #E2DFD6` }}>
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: "#F0EDE5" }}>
                {["Ingrediente", "Fornitore", "Lotto", "Scadenza", "Q.tà", "Stato"].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold" style={{ color: COL.ink }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {ings.map((it, i) => {
                  const st = expiryStatus(it.expiry);
                  return (
                    <tr key={i} style={{ borderTop: "1px solid #E9E6DD" }} data-testid={`ing-row-${i}`}>
                      <td className="px-3 py-2 font-semibold" style={{ color: COL.ink }}>{it.name}</td>
                      <td className="px-3 py-2" style={{ color: COL.sub }}>{it.supplier || "—"}</td>
                      <td className="px-3 py-2" style={{ color: COL.ink }}>{it.lotto || "—"}</td>
                      <td className="px-3 py-2" style={{ color: COL.ink }}>{fmtDate(it.expiry)}</td>
                      <td className="px-3 py-2" style={{ color: COL.ink }}>{it.quantity ? `${it.quantity} ${it.unit || ""}` : "—"}</td>
                      <td className="px-3 py-2"><StatusBadge s={st} /></td>
                    </tr>
                  );
                })}
                {ings.length === 0 && <tr><td colSpan={6} className="px-3 py-3 text-center" style={{ color: COL.sub }}>Nessun ingrediente</td></tr>}
              </tbody>
            </table>
          </div>

          {/* ALLERGENI */}
          {(s.allergens || []).length > 0 && (
            <div className="mt-6">
              <h3 className="font-bold mb-2 flex items-center gap-1.5 text-base" style={{ color: COL.ink }}><AlertTriangle className="w-4 h-4" style={{ color: COL.gold }} /> Allergeni presenti</h3>
              <div className="flex flex-wrap gap-2">
                {s.allergens.map((a) => <span key={a} className="text-xs font-semibold px-3 py-1 rounded-full border" style={{ color: COL.ink, backgroundColor: "#FFFFFF", borderColor: COL.gold }} data-testid={`allergen-${a}`}>{a.toUpperCase()}</span>)}
              </div>
            </div>
          )}

          {/* PROCEDURA */}
          {s.procedure && (
            <div className="mt-6">
              <h3 className="font-bold mb-2 text-base" style={{ color: COL.ink }}>Procedura di preparazione</h3>
              <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: COL.ink }}>{s.procedure}</p>
            </div>
          )}

          <p className="text-[10px] mt-7 leading-relaxed" style={{ color: COL.sub }}>Documento conforme a Reg. CE 178/2002, 852/2004 e Reg. UE 1169/2011 (allergeni). {s.business?.address}{s.business?.vat ? " · P.IVA " + s.business.vat : ""}</p>
        </div>
      </div>
    </div>
  );
}
